import { chronicleIsRecordingAtom } from '@/atoms/chronicle';
import { isGenerationInFlight } from '@/components/utils/sessionTimeout';
import { useQuerySessionStreamingStatus } from '@/hooks/query/useQuerySessionStatesById';
import { type Dispatch, type RootState } from '@/store';
import { toast } from '@heidi/design-system/toast';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useIdle } from '@uidotdev/usehooks';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

interface Props {
  // in minutes
  timeToLogout: number;
}
export function AutoLogout(props: Props) {
  const { timeToLogout } = props;
  const token = useSelector((state: RootState) => state.auth.token);
  const dispatch = useDispatch<Dispatch>();
  const sessionId = useCurrentSessionId();
  const streamingStatus = useQuerySessionStreamingStatus(sessionId);
  const isGenerating = isGenerationInFlight(streamingStatus);
  const isRunning = useSelector((rootState: RootState) => rootState.timerModel.isRunning);
  const isChronicleActive = useAtomValue(chronicleIsRecordingAtom);

  // if idle for 25 minutes, show modal
  const isIdleForLongPeriod = useIdle(1000 * 60 * timeToLogout);

  const shouldLogoutOnNoActivity =
    !isRunning && !isGenerating && !isChronicleActive && Boolean(timeToLogout);

  const logoutOnNoActivity = () => {
    if (!token) {
      return;
    }
    if (!shouldLogoutOnNoActivity) {
      return;
    }
    void dispatch.auth.logout();
    toast({
      tone: 'destructive',
      title: 'You have been logged out due to inactivity',
      description: 'Please log in again to continue using Heidi',
    });
  };

  const debouncedLogoutOnNoActivity = useDebouncedCallback(logoutOnNoActivity, 2000, {
    leading: true,
    trailing: false,
  });

  useEffect(() => {
    if (!shouldLogoutOnNoActivity) {
      return;
    }
    if (isIdleForLongPeriod) {
      debouncedLogoutOnNoActivity();
    }
  }, [isIdleForLongPeriod, debouncedLogoutOnNoActivity, shouldLogoutOnNoActivity]);

  return <></>;
}

export default AutoLogout;
